import React, { useState } from "react";
import { ModalShell } from "./ModalShell";
import { HiOutlineShare } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import QRCodeGenerator from "./QRCodeGenerator";
import CopyToClipboardButton from "./CopyToClipboardButton";
import { CustomButton } from "./CustomButton";
import { useCardPreview } from "../../contexts/CardPreviewContext";

interface BusinessCardShareButtonProps {
  processId: string;
  className?: string;
  size?: "xs" | "sm" | "md" | "lg";
  variant?: "primary" | "secondary" | "minimal";
  logoUrl?: string;
  showLabel?: boolean;
}

const BusinessCardShareButton: React.FC<BusinessCardShareButtonProps> = ({
  processId,
  className = "",
  size = "sm",
  variant = "secondary",
  logoUrl = "https://cdn.1cdn.app/application/UMD/2405110558455_logo.png",
  showLabel = false,
}) => {
  const { t } = useTranslation("clientView");
  const isPreview = useCardPreview();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showManualFallback, setShowManualFallback] = useState(false);
  const businessCardUrl = `${process.env.REACT_APP_HOST_URL}/process/${processId}`;

  const handleCloseModal = () => {
    setShowManualFallback(false);
    setShowShareModal(false);
  };

  const getButtonProps = () => {
    const baseProps = {
      size,
      onClick: isPreview ? undefined : () => setShowShareModal(true),
      disabled: isPreview,
      tabIndex: isPreview ? -1 : undefined,
      "aria-hidden": isPreview ? true : undefined,
    };

    switch (variant) {
      case "primary":
        return {
          ...baseProps,
          variant: "primary" as const,
          className: `transition-all duration-200 ${className}`,
        };
      case "minimal":
        return {
          ...baseProps,
          variant: "none" as const,
          className: `transition-all duration-200 text-gray-600 hover:text-blue-600 ${className}`,
        };
      case "secondary":
      default:
        return {
          ...baseProps,
          variant: "secondary" as const,
          className: `transition-all duration-200 shadow-sm hover:shadow-sm ${className}`,
        };
    }
  };

  const buttonProps = getButtonProps();

  return (
    <>
      <CustomButton
        {...buttonProps}
        icon={<HiOutlineShare size={showLabel ? 18 : 16} />}
        title={t("key86")}
        titleClassName={showLabel ? undefined : "hidden sm:flex"}
        tooltip={t("key86") as string}
      />

      <ModalShell
        show={showShareModal}
        onClose={handleCloseModal}
        size="md"
        rootClassName="z-50"
        title={
          <span className="flex min-w-0 items-center gap-2">
            <HiOutlineShare className="flex-shrink-0 text-blue-600" size={18} />
            <span className="truncate">{t("key87")}</span>
          </span>
        }
        footer={
          <CustomButton
            variant="secondary"
            onClick={handleCloseModal}
            size="sm"
            title={t("key92")}
          />
        }
      >
        <div className="grid grid-rows-[auto_auto_auto] gap-3 sm:gap-4 text-center">
          <div className="grid justify-items-center gap-2">
            <QRCodeGenerator
              data={businessCardUrl}
              size={250}
              showLogo={!!logoUrl}
              logoUrl={logoUrl}
              className="mb-2"
              ariaLabel={`QR code for business card`}
            />
            <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-1">
              {t("key88")}
            </h3>
            <p className="text-gray-600 text-xs sm:text-sm max-w-xs sm:max-w-sm px-2 leading-tight">
              {t("key89")}
            </p>
          </div>

          <div className="relative mx-4">
            <div className="absolute inset-0 grid items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative grid justify-center text-xs uppercase">
              <span className="bg-white px-2 text-gray-500">{t("key90")}</span>
            </div>
          </div>

          <div className="grid justify-items-center px-4 gap-2">
            <CopyToClipboardButton
              translationNamespace="clientView"
              textToCopy={businessCardUrl}
              onSuccess={() => setShowManualFallback(false)}
              onError={() => setShowManualFallback(true)}
              disabled={!processId}
            />

            {showManualFallback && (
              <div className="w-full max-w-sm">
                <p className="text-xs text-gray-600 mb-2 text-center">
                  {t("key91")}
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                  <input
                    type="text"
                    value={businessCardUrl}
                    readOnly
                    className="w-full text-base md:text-xs bg-transparent border-0 focus:ring-0 p-1 text-gray-700 select-all"
                    onClick={(e) => e.currentTarget.select()}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </ModalShell>
    </>
  );
};

export default BusinessCardShareButton;
